import React, { useState, useEffect, useCallback } from 'react'
import { Map as ImmutableMap } from 'immutable'
import { Box, Typography, Alert, TextField } from '@mui/material'
import MuiButton from 'gui/components/common/MuiButton'
import { MARKETING_PLAN_PROMPT } from 'services/prompts/prompts'
import { useApi } from '../ai-chat/hooks/useApi'
import {
  useTranscriptFetcher,
  ProgressTracker,
  CollapsibleSection,
  type ProgressStep,
  type OpenAIResponse,
} from './ai-helpers'

/**
 * Component props
 */
interface AIMarketingPlanGenerationProps {
  /** Immutable Map containing content data */
  content: ImmutableMap<string, unknown>
  /** Whether the form is in view-only mode */
  isViewOnlyMode: boolean
  /** ID of the attached media */
  featureMediaId?: number | string | null
}

/**
 * AI Marketing Plan Generation Component
 *
 * Allows users to generate AI-powered marketing plans from video transcripts.
 * Users can fetch the transcript, generate a marketing plan via AI, and view/copy the result.
 */
function AIMarketingPlanGeneration ({
  content,
  isViewOnlyMode,
  featureMediaId,
}: AIMarketingPlanGenerationProps) {
  const { makeRequest } = useApi()
  const {
    fetchTranscript,
    transcript,
    loading: transcriptLoading,
    error: transcriptError,
  } = useTranscriptFetcher()
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [generatedPlan, setGeneratedPlan] = useState<string | null>(null)
  const [promptExpanded, setPromptExpanded] = useState<boolean>(false)
  const [transcriptExpanded, setTranscriptExpanded] = useState<boolean>(false)
  const [editablePrompt, setEditablePrompt] = useState<string>(MARKETING_PLAN_PROMPT)
  const [currentStep, setCurrentStep] = useState<ProgressStep | null>(null)
  const [completedSteps, setCompletedSteps] = useState<ProgressStep[]>([])

  /**
   * Generate marketing plan from transcript
   * Calls OpenAI via Brooklyn's proxy endpoint with the transcript and marketing plan prompt
   */
  const generateMarketingPlan = useCallback(
    async (transcriptText: string): Promise<void> => {
      try {
        // Build the full user prompt by combining the editable prompt with the transcript
        const userPrompt = `${editablePrompt}\n\n${transcriptText}`

        // Call OpenAI via Brooklyn's proxy endpoint
        // This uses the /openai/* endpoint which forwards to OpenAI's API
        const response = (await makeRequest('/openai/v1/chat/completions', {
          body: {
            model: 'gpt-4o',
            messages: [
              {
                role: 'system',
                content:
                  'You are a marketing expert for Gaia. Create comprehensive marketing plans based on video content. Return the marketing plan as formatted text/markdown.',
              },
              {
                role: 'user',
                content: userPrompt,
              },
            ],
            temperature: 0.7,
          },
        })) as unknown as OpenAIResponse

        // Parse the OpenAI response
        // OpenAI returns: { choices: [{ message: { content: "..." } }] }
        const aiContent = response?.choices?.[0]?.message?.content

        if (!aiContent) {
          throw new Error('No content in OpenAI response')
        }

        setGeneratedPlan(aiContent)
        setCompletedSteps((prev) => [...prev, 'generating'])
        setCurrentStep('complete')
        setLoading(false)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error'
        setError(`Failed to generate marketing plan: ${errorMessage}`)
        setLoading(false)
        setCurrentStep(null)
      }
    },
    [editablePrompt, makeRequest],
  )

  // Handle transcript fetching results
  useEffect(() => {
    if (!transcript && !transcriptError && !transcriptLoading) return

    const processTranscript = async () => {
      if (transcriptError) {
        setError(transcriptError)
        setLoading(false)
        setCurrentStep(null)
        return
      }

      if (transcript) {
        // Mark fetching step as complete
        setCompletedSteps((prev) => [...prev, 'fetching'])
        setCurrentStep('generating')

        // Call AI service to generate marketing plan
        await generateMarketingPlan(transcript)
      }
    }

    processTranscript()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript, transcriptError, transcriptLoading])

  /**
   * Handle generate marketing plan button click
   * Fetches the transcript and prepares for AI generation
   */
  const handleGeneratePlan = (): void => {
    const contentId = content.get('contentId') as number | string | undefined

    if (!contentId) {
      setError('Content ID not found')
      return
    }

    // Clear previous errors and data
    setError(null)
    setGeneratedPlan(null)
    setLoading(true)
    setCurrentStep('fetching')
    setCompletedSteps([])

    // Fetch the transcript
    fetchTranscript(contentId)
  }

  /**
   * Handle retry button click
   * Clears error and retries the operation
   */
  const handleRetry = (): void => {
    setError(null)
    handleGeneratePlan()
  }

  /**
   * Copy marketing plan to clipboard
   */
  const handleCopyToClipboard = async (): Promise<void> => {
    if (!generatedPlan) return

    try {
      await navigator.clipboard.writeText(generatedPlan)
      // Could show a toast notification here if desired
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to copy to clipboard: ${errorMessage}`)
    }
  }

  // Determine if button should be disabled
  const isButtonDisabled = !featureMediaId || isViewOnlyMode || loading

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6" gutterBottom>
        AI Marketing Plan Generation
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Generate a comprehensive marketing plan for this video using AI. Using the prompt below,
        fetch data from OpenAI.
      </Typography>

      {/* Collapsible Prompt Editor */}
      <CollapsibleSection
        title="Prompt"
        expanded={promptExpanded}
        onToggle={() => setPromptExpanded(!promptExpanded)}
      >
        <Box sx={{ p: 2, pt: 0 }}>
          <TextField
            fullWidth
            multiline
            rows={12}
            value={editablePrompt}
            onChange={(e) => setEditablePrompt(e.target.value)}
            disabled={loading || isViewOnlyMode}
            variant="outlined"
            sx={{
              backgroundColor: '#fff',
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.85rem',
              },
            }}
          />
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <MuiButton
              size="small"
              variant="outlined"
              onClick={() => setEditablePrompt(MARKETING_PLAN_PROMPT)}
              disabled={loading || isViewOnlyMode}
            >
              Reset to Default
            </MuiButton>
          </Box>
        </Box>
      </CollapsibleSection>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <MuiButton color="primary" size="small" onClick={handleRetry}>
              Retry
            </MuiButton>
          }
        >
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <MuiButton
          variant="contained"
          color="primary"
          onClick={handleGeneratePlan}
          disabled={isButtonDisabled}
        >
          Generate Marketing Plan from AI
        </MuiButton>
      </Box>

      {/* Progress Tracker */}
      {(loading || currentStep) && (
        <ProgressTracker
          steps={[
            { key: 'fetching', label: 'Fetching Transcript' },
            {
              key: 'generating',
              label: 'Generating Marketing Plan',
              loadingLabel: 'Generating Marketing Plan (this takes a moment...)',
            },
          ]}
          currentStep={currentStep}
          completedSteps={completedSteps}
        />
      )}

      {/* Collapsible Transcript Viewer */}
      {transcript && (
        <CollapsibleSection
          title={`Transcript (${transcript.length.toLocaleString()} characters)`}
          expanded={transcriptExpanded}
          onToggle={() => setTranscriptExpanded(!transcriptExpanded)}
        >
          <Box
            sx={{
              p: 2,
              pt: 0,
              maxHeight: 300,
              overflowY: 'auto',
              backgroundColor: '#fff',
              borderTop: '1px solid #e0e0e0',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap',
            }}
          >
            {transcript}
          </Box>
        </CollapsibleSection>
      )}

      {/* Generated Marketing Plan Display */}
      {generatedPlan && (
        <Box
          sx={{
            mt: 3,
            p: 3,
            backgroundColor: '#f5f5f5',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
          }}
        >
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">Generated Marketing Plan</Typography>
            <MuiButton
              variant="outlined"
              size="small"
              onClick={handleCopyToClipboard}
              disabled={isViewOnlyMode}
            >
              Copy to Clipboard
            </MuiButton>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={20}
            value={generatedPlan}
            InputProps={{
              readOnly: true,
            }}
            variant="outlined"
            sx={{
              backgroundColor: '#fff',
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.9rem',
              },
            }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {generatedPlan.length.toLocaleString()} characters,{' '}
            {generatedPlan
              .split(/\s+/)
              .filter((word) => word.length > 0)
              .length.toLocaleString()}{' '}
            words
          </Typography>
        </Box>
      )}

      {!featureMediaId && !isViewOnlyMode && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Please attach media to this video before generating a marketing plan.
        </Alert>
      )}
    </Box>
  )
}

export default AIMarketingPlanGeneration
